import { Link } from "react-router-dom";
import { companyName, supportEmail } from "@/lib/companyBrand";
import { PRICING_LINK_VISIBLE } from "@/lib/featureFlags";
import { IOS_CLIENT, linkForClient } from "@/lib/guideClient";

// `ios: true` marks a page that has an iOS mode and sells nothing. A page
// opened from the iOS app links only to those, so a link added later stays
// off the app's pages until someone has checked where it leads.
const footerSections = [
  {
    title: "Product",
    links: [
      { label: "Lesson summaries", to: "/features/lesson-summaries" },
      { label: "Student Management", to: "/features/student-management" },
      { label: "Repertoire Assistant", to: "/features/repertoire-assistant" },
      { label: "Joining a Program", to: "/features/joining-a-program" },
      { label: "Email Summaries", to: "/features/email-summaries" },
      { label: "Student Portal", to: "/features/student-portal" },
      { label: "Rehearsals", to: "/features/rehearsals" },
      { label: "Portfolio", to: "/features/portfolio" },
      { label: "Hans for Organizations", to: "/for-organizations" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Onboarding videos", to: "/academy/onboarding" },
      { label: "Instructor guide", to: "/guide", ios: true },
      { label: "Student & parent guide", to: "/student-guide", ios: true },
      { label: "Organization guide", to: "/org-guide" },
      { label: "Program viewer guide", to: "/viewer-guide" },
      { label: "Support", to: "/support", ios: true },
      { label: "Feedback", to: "/feedback", ios: true },
      { label: "Contact", to: "/contact", ios: true },
      // Hidden alongside the navbar entry while PRICING_LINK_VISIBLE is off —
      // leaving it here would keep the page one click away from every page.
      // Never `ios`, so a page opened in the iOS app drops it either way.
      ...(PRICING_LINK_VISIBLE ? [{ label: "Pricing", to: "/pricing" }] : []),
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy", ios: true },
      { label: "Terms of Service", to: "/terms", ios: true },
      { label: "Security", to: "/security", ios: true },
    ],
  },
];

/**
 * The footer's link columns for a client. The iOS app gets only the links
 * marked `ios`, each keeping its mode, and no column that is left empty.
 */
const footerSectionsFor = (client) =>
  client === IOS_CLIENT
    ? footerSections
        .map((section) => ({
          ...section,
          links: section.links
            .filter((link) => link.ios)
            .map((link) => ({ ...link, to: linkForClient(link.to, client) })),
        }))
        .filter((section) => section.links.length > 0)
    : footerSections;

const PublicFooter = ({ client = null }) => (
  <footer className="relative z-10 border-t border-white/5 px-4 md:px-8 py-12 md:py-16 safe-area-padding">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
        {footerSectionsFor(client).map((section) => (
          <div key={section.title}>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4">{section.title}</p>
            <ul className="space-y-2.5">
              {section.links.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-zinc-400 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Hans branding column */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo192.png" alt="Hans" className="w-8 h-8 rounded-lg" />
            <span className="text-base font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Hans</span>
          </div>
          <p className="text-xs text-zinc-500 mb-2">The digital studio for the dedicated artist.</p>
          <p className="mb-3 text-xs leading-6 text-[var(--text-secondary)]">
            Hans helps music teachers record lessons, share summaries and manage
            their studios. Its booking service,{" "}
            {/* Named but not linked in the iOS app: Scheduler is a separate
                site with its own sign-in and plans. */}
            {client === IOS_CLIENT ? (
              "Kalver Scheduler"
            ) : (
              <a href="https://scheduler.kalver.ai" className="text-[var(--accent-yellow-text)] hover:underline">
                Kalver Scheduler
              </a>
            )}, offers optional Google Calendar sync to keep appointments and
            availability aligned.
          </p>
          <a
            href={`mailto:${supportEmail()}`}
            className={"text-xs text-zinc-500 hover:text-[#f4cf08] transition-colors" /* brand-lint-ignore: legacy idiom */}
          >
            {supportEmail()}
          </a>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-zinc-600 text-xs font-mono tracking-wider">
          by {companyName()} &copy; {new Date().getFullYear()}
        </p>
        <div className="flex items-center gap-6">
          <Link to={linkForClient("/privacy", client)} className={"text-xs text-zinc-600 hover:text-zinc-400 transition-colors" /* brand-lint-ignore: legacy idiom */}>Privacy</Link>
          <Link to={linkForClient("/terms", client)} className={"text-xs text-zinc-600 hover:text-zinc-400 transition-colors" /* brand-lint-ignore: legacy idiom */}>Terms</Link>
          <Link to={linkForClient("/security", client)} className={"text-xs text-zinc-600 hover:text-zinc-400 transition-colors" /* brand-lint-ignore: legacy idiom */}>Security</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default PublicFooter;
